/**
 * One-shot UI hints: visible until dismissed, then persisted away for good.
 * Storage seam mirrors usePaletteHistory — private mode degrades to
 * visible-once-per-session.
 */

import { ref } from 'vue';
import type { Ref } from 'vue';
import { useBuilderHost } from './host';

export interface HintStorage {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
}

export interface OneTimeHint {
    visible: Ref<boolean>;
    dismiss(): void;
}

export function makeOneTimeHint(key: string, storage?: HintStorage | null, storagePrefix = 'pb2'): OneTimeHint {
    const store = storage ?? (typeof localStorage === 'undefined' ? null : localStorage);
    const storageKey = `${storagePrefix}:hint:${key}`;

    let seen = false;

    try {
        seen = store?.getItem(storageKey) === '1';
    } catch {
        // private mode / quota — hint shows once per session
    }

    const visible = ref(!seen);

    return {
        visible,
        dismiss() {
            visible.value = false;

            try {
                store?.setItem(storageKey, '1');
            } catch {
                // private mode / quota — dismissal lasts the session only
            }
        },
    };
}

export function useOneTimeHint(key: string, storage?: HintStorage | null): OneTimeHint {
    return makeOneTimeHint(key, storage, useBuilderHost().storagePrefix ?? 'pb2');
}
